import { motion } from "framer-motion";
import { Users, Target, Award, Shield } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";

const AboutPage = () => {
  const values = [
    {
      icon: Users,
      title: "Partner-Led",
      description: "Every client works directly with a senior partner who understands your business and objectives."
    },
    {
      icon: Target,
      title: "Precision Focus",
      description: "We specialise exclusively in property finance, ensuring deep expertise and lender relationships."
    },
    {
      icon: Award,
      title: "Institutional Quality",
      description: "Deals packaged to bank-grade standards with comprehensive feasibility and risk analysis."
    },
    {
      icon: Shield,
      title: "Trusted Advisor",
      description: "Long-term partnerships built on transparency, integrity, and aligned interests."
    }
  ];

  const team = [
    {
      name: "David P",
      role: "Founding Partner"
    },
    {
      name: "John P",
      role: "Founding Partner"
    },
    {
      name: "Airlie C",
      role: "Founding Partner"
    }
  ];

  return (
    <div className="relative min-h-screen flex flex-col" data-testid="about-page">
      <Navigation />
      
      <main className="flex-1 relative z-10 px-8 lg:px-16 pt-40 pb-24">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto mb-20"
          >
            <h1 
              className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-medium text-white tracking-tight mb-6"
              data-testid="about-heading"
            >
              About Solara
            </h1>
            <p className="text-lg md:text-xl font-light leading-relaxed text-slate-300">
              We're a partner-led finance advisory firm dedicated to helping Australia's 
              property leaders secure the capital they need to build, acquire, and grow.
            </p>
          </motion.div>

          {/* Story Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="glass-card p-8 md:p-12 rounded-sm mb-16"
          >
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-white font-medium mb-6">
                  Our Story
                </h2>
                <p className="text-slate-300 leading-relaxed mb-4">
                  Solara Global Capital was founded with a singular vision: to bring institutional-grade 
                  service to Australia's property developers and strategic acquirers.
                </p>
                <p className="text-slate-400 leading-relaxed mb-4">
                  Our founders spent decades in major banks and property finance institutions, 
                  witnessing firsthand how developers often struggled with opaque processes, 
                  misaligned advice, and inconsistent execution.
                </p>
                <p className="text-slate-400 leading-relaxed">
                  We built Solara to be different. Partner-led from day one, with AI-enabled 
                  processes that provide clarity and certainty throughout your funding journey.
                </p>
              </div>
              <div className="relative">
                <div className="aspect-square rounded-sm overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800"
                    alt="Sydney skyline"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="text-[#bd9a59] text-sm font-semibold uppercase tracking-wider mb-1">
                    Sydney, Australia
                  </p>
                  <p className="text-white text-lg font-medium">
                    60 Martin Place
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Values Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mb-16"
          >
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-white font-medium text-center mb-12">
              Our Values
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                  className="glass-card p-6 rounded-sm hover:border-[#bd9a59]/30 transition-colors"
                  data-testid={`value-card-${index}`}
                >
                  <value.icon className="w-8 h-8 text-[#bd9a59] mb-4" />
                  <h3 className="text-white font-semibold text-lg mb-2">{value.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Team Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-white font-medium text-center mb-12">
              Leadership Team
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {team.map((member, index) => (
                <motion.div
                  key={member.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
                  className="glass-card p-6 rounded-sm text-center"
                  data-testid={`team-card-${index}`}
                >
                  <div className="w-20 h-20 rounded-full bg-slate-800 mx-auto mb-4 flex items-center justify-center">
                    <span className="text-2xl font-medium text-[#bd9a59]">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-1">{member.name}</h3>
                  <p className="text-[#bd9a59] text-sm font-medium">{member.role}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
